/**
 * IIDM-3064 T1 — the ONE place the portal's SSO access token is read, written and removed.
 *
 * It used to be captured ONCE at module load (`export const token = getLocalStorage("token")` in
 * services/api-services.tsx) and imported by ~14 files. A token written after load — every login,
 * because login writes the token and then relies on `window.location.reload()` to re-evaluate that
 * constant — was invisible until the next full page load. Every reader now asks this module, per
 * call, so there is nothing to go stale.
 *
 * Storage (plan Q1, approved default): `sessionStorage` — per tab, gone when the tab closes. No
 * refresh token is stored. A new tab has no token and silently re-runs the SSO authorize redirect,
 * which the SSO session answers without a prompt. Any browser storage is the same XSS class; the
 * real mitigations are a short token TTL (IIDM-3063), CSP / sanitising (IIDM-3066) and revoke on
 * logout (`logout.ts`).
 */
const TOKEN_KEY = "token";

/**
 * @return {string} the current access token, or "" when there is none (callers compare to "").
 */
export function getToken(): string {
  try {
    return window.sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (e) {
    return "";
  }
}

/**
 * @param {string} value the access token returned by the SSO token endpoint
 */
export function setToken(value: string): void {
  try {
    window.sessionStorage.setItem(TOKEN_KEY, value);
  } catch (e) {
    // storage blocked: the user re-authorizes on the next load, which is the safe outcome.
  }
}

/**
 * Removes the token from BOTH storages: sessionStorage (current) and localStorage, where every
 * build before IIDM-3064 kept it. Without the second removal a token left behind by an older
 * build would outlive logout.
 */
export function clearToken(): void {
  try {
    window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // ignore
  }
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // ignore
  }
}
